/**
 * 应答指南（`#guidance`）：生效画像版本里的全部指南只读展示（后台页面对齐批 3；摸底 C_应答配置 第 8 条）。
 * 线上每轮按条件注入 N2 ／ N3（`runtime/guide_render.py`）：常备的每轮都带、must 在前、一次为限按会话记；
 * 召回（画像 pv5 起，标签体系 v2 批 4）：按「适用条件」文字逐条判和这件事对不对得上（相关性判断），意图标签与情绪风险标签
 * 只加分；数据状态 ／ 信息项条件是系统事实，每步决策前再按事项的事实补。旧画像：写了意图的按意图挂，没写的按候选工具挂。
 * 读的是生效版本（`recall/profiles.active_version()`），不写死 pv2。召回条件在「候选召回」页改、存新画像版本。
 */
import { useMemo, useState } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import {
	type PlanGuide,
	type PlanLayer,
	type RecallGuide,
	planErrorMessage,
	usePlanGuidance,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import {
	LAYER_ALL,
	LAYER_FALLBACK,
	LayerBadge,
	LayerDetail,
	layerOptions,
	matchLayer,
} from "../kit/layer";
import { PageJump } from "../kit/page-jump";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";
import { ENTITY_LABEL, STATE_LABEL, labelOf, slotCondText } from "../recall/recall-labels";

const ALL = "__all__";

const NODE_LABEL: Record<string, string> = { n2: "N2 决策", n3: "N3 回复", both: "N2 ＋ N3" };
const STRENGTH: Record<string, { label: string; tone: BadgeTone }> = {
	must: { label: "必须 must", tone: "danger" },
	should: { label: "应该 should", tone: "warning" },
	prefer: { label: "倾向 prefer", tone: "muted" },
};
const STATUS: Record<string, { label: string; tone: BadgeTone }> = {
	live: { label: "生效", tone: "success" },
	retired: { label: "退役", tone: "muted" },
};

const nodeOf = (g: PlanGuide) => g.node || "both";
const statusOf = (g: PlanGuide) => g.status || "live";
const modeText = (g: PlanGuide) => (g.mode === "once" ? "一次为限" : "每轮");
/** 每步决策前按事实补（`guide_render.by_facts`）：带数据条件或「in ／ class」型信息项条件的 */
export const byFacts = (g: RecallGuide) =>
	!g.standing && (!!g.data_conds?.length || !!g.slot_conds?.some((c) => c.in || c.class));

/** 指南的意图标签：pv5 起 intent_tags（只加分），旧画像 intents（挂载卡口） */
export const guideTags = (g: RecallGuide) => g.intent_tags ?? g.intents ?? [];

/** 情绪与风险标签（pv5 起，只加分）的显示串 */
export function signalItems(
	g: RecallGuide,
	names?: Record<"sentiment" | "risk_flags", Record<string, string>>,
): string[] {
	const t = g.signal_tags ?? {};
	return [
		...(t.sentiment ?? []).map((k) => `情绪${names?.sentiment?.[k] ?? k}`),
		...(t.risk_flags ?? []).map((k) => names?.risk_flags?.[k] ?? k),
	];
}

/** 结构化条件（实体 ／ 数据 ／ 对话 ／ 信息项）的显示串 */
function condItems(g: PlanGuide): string[] {
	return [
		...(g.entity_conds ?? []).map(
			(c) => `${labelOf(ENTITY_LABEL, c.entity)}${c.present ? "有" : "无"}`,
		),
		...(g.data_conds ?? []).map((s) => labelOf(STATE_LABEL, s)),
		...(g.dialogue_conds ?? []).map((s) => labelOf(STATE_LABEL, s)),
		...(g.slot_conds ?? []).map(slotCondText),
	];
}

/** 强度徽标：表格里只显示 must ／ should ／ prefer（与注入原文的 MUST 对得上），抽屉里带中文 */
function StrengthBadge({ g, short = false }: { g: PlanGuide; short?: boolean }) {
	const s = STRENGTH[g.strength ?? ""];
	if (!s) return <span>{g.strength ?? "—"}</span>;
	return (
		<span title={s.label}>
			<Badge tone={s.tone}>{short ? g.strength : s.label}</Badge>
		</span>
	);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="whitespace-pre-wrap text-sm text-text-primary">{children}</div>
		</div>
	);
}

function Trunc({ text, mono = false }: { text?: string | null; mono?: boolean }) {
	if (!text) return <span className="text-text-muted">—</span>;
	return (
		<span
			className={`block truncate text-text-secondary${mono ? " font-mono text-xs" : ""}`}
			title={text}
		>
			{text}
		</span>
	);
}

export function GuidanceTab() {
	const q = usePlanGuidance();
	const [node, setNode] = useState(ALL);
	const [strength, setStrength] = useState(ALL);
	const [standing, setStanding] = useState(ALL);
	const [status, setStatus] = useState(ALL);
	const [intent, setIntent] = useState(ALL);
	const [layer, setLayer] = useState(LAYER_ALL);
	const [open, setOpen] = useState<PlanGuide | null>(null);
	const data = q.data;
	const intents = data?.intents ?? {};
	const intentName = (k: string) => intents[k] ?? k;
	const layers = data?.layers?.length ? data.layers : LAYER_FALLBACK;

	const all = data?.guides ?? [];
	const rows = useMemo(
		() =>
			all.filter(
				(g) =>
					(node === ALL || nodeOf(g) === node || nodeOf(g) === "both") &&
					(strength === ALL || g.strength === strength) &&
					(standing === ALL || (standing === "yes") === !!g.standing) &&
					(status === ALL || statusOf(g) === status) &&
					(intent === ALL || guideTags(g).includes(intent)) &&
					matchLayer(layer, g, layers),
			),
		[all, node, strength, standing, status, intent, layer, layers],
	);
	const usedIntents = useMemo(() => [...new Set(all.flatMap(guideTags))], [all]);

	// 三态都打一级块标记 plan-block-guidance（布局量测 C1～C3 靠它找块）
	if (q.isLoading)
		return (
			<Card data-testid="plan-block-guidance">
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError || !data)
		return (
			<Card data-testid="plan-block-guidance">
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-guidance-fail"
					/>
				</CardContent>
			</Card>
		);

	const live = all.filter((g) => statusOf(g) === "live");
	const cols: PlanColumn<PlanGuide>[] = [
		{
			key: "id",
			title: "编号",
			className: "max-w-[180px]",
			render: (g) => <Trunc text={g.id} mono />,
		},
		{
			key: "title",
			title: "标题",
			className: "max-w-[150px]",
			render: (g) => (
				<span className="block truncate" title={g.title}>
					{g.title}
				</span>
			),
		},
		{
			key: "layer",
			title: "层",
			className: "w-24",
			render: (g) => <LayerBadge row={g} layers={layers} />,
		},
		{ key: "node", title: "节点", render: (g) => labelOf(NODE_LABEL, nodeOf(g)) },
		{
			key: "strength",
			title: "强度",
			render: (g) => <StrengthBadge g={g} short />,
		},
		{
			key: "standing",
			title: "常备",
			render: (g) =>
				g.standing ? <Badge tone="info">常备</Badge> : <span className="text-text-muted">—</span>,
		},
		{ key: "mode", title: "次数", render: modeText },
		{
			key: "status",
			title: "状态",
			render: (g) => {
				const s = STATUS[statusOf(g)];
				return s ? <Badge tone={s.tone}>{s.label}</Badge> : statusOf(g);
			},
		},
		{
			key: "mount",
			title: "召回",
			render: (g) => <Mount g={g} intentName={intentName} />,
		},
		{
			key: "cond",
			title: "适用条件",
			className: "max-w-[140px]",
			render: (g) => <Trunc text={g.condition_text} />,
		},
		{
			key: "action",
			title: "做法",
			className: "max-w-[180px]",
			render: (g) => <Trunc text={g.action} />,
		},
		{
			key: "note",
			title: "出处说明",
			className: "max-w-[110px]",
			render: (g) => <Trunc text={g.rule_note} />,
		},
	];

	const opt = (xs: [string, string][]) => [
		{ value: ALL, label: "全部" },
		...xs.map(([value, label]) => ({ value, label })),
	];
	return (
		<Card data-testid="plan-block-guidance">
			<CardContent className="space-y-3 py-4" data-testid="plan-guidance">
				<div className="flex flex-wrap items-center gap-2">
					<LabeledSelect
						label="节点"
						value={node}
						onChange={setNode}
						options={opt([
							["n2", "N2 决策"],
							["n3", "N3 回复"],
						])}
					/>
					<LabeledSelect
						label="强度"
						value={strength}
						onChange={setStrength}
						options={opt([
							["must", "必须 must"],
							["should", "应该 should"],
							["prefer", "倾向 prefer"],
						])}
					/>
					<LabeledSelect
						label="常备"
						value={standing}
						onChange={setStanding}
						options={opt([
							["yes", "常备"],
							["no", "非常备"],
						])}
					/>
					<LabeledSelect
						label="状态"
						value={status}
						onChange={setStatus}
						options={opt([
							["live", "生效"],
							["retired", "退役"],
						])}
					/>
					<LabeledSelect
						label="意图标签"
						value={intent}
						onChange={setIntent}
						options={opt(usedIntents.map((k) => [k, intentName(k)]))}
					/>
					<span data-testid="plan-guidance-layer-filter">
						<LabeledSelect
							label="层"
							value={layer}
							onChange={setLayer}
							options={layerOptions(layers, all)}
						/>
					</span>
					<span className="ml-auto text-xs text-text-muted" data-testid="plan-guidance-count">
						画像 <span className="font-mono">{data.version}</span> · 生效 {live.length} · 常备{" "}
						{live.filter((g) => g.standing).length} · N2{" "}
						{live.filter((g) => nodeOf(g) !== "n3").length} · N3{" "}
						{live.filter((g) => nodeOf(g) !== "n2").length} · 当前 {rows.length} 条
					</span>
				</div>
				{rows.length ? (
					<PlanTable
						columns={cols}
						rows={rows}
						rowKey={(g) => g.id}
						onRowClick={setOpen}
						density="compact"
						rowTestId="plan-guidance-row"
					/>
				) : (
					<EmptyState title="没有符合条件的指南" desc="换个节点、强度或意图看看。" />
				)}
			</CardContent>
			<Drawer
				open={!!open}
				onClose={() => setOpen(null)}
				width="min(760px,95vw)"
				testId="plan-guidance-drawer"
				title={
					<span>
						<span className="font-mono">{open?.id}</span> {open?.title}
					</span>
				}
				badges={
					open ? (
						<>
							<Badge tone="neutral">{labelOf(NODE_LABEL, nodeOf(open))}</Badge>
							<StrengthBadge g={open} />
							{open.standing ? <Badge tone="info">常备</Badge> : null}
							<Badge tone={STATUS[statusOf(open)]?.tone ?? "muted"}>
								{STATUS[statusOf(open)]?.label ?? statusOf(open)}
							</Badge>
						</>
					) : null
				}
			>
				{open ? (
					<GuideDetail
						g={open}
						intentName={intentName}
						signalNames={data.signal_names}
						layers={layers}
					/>
				) : null}
			</Drawer>
		</Card>
	);
}

/** 召回：常备每轮都带；其余按相关性判断（意图标签只加分）；带数据 ／ 信息项条件的每步再按事实补 */
export function Mount({
	g,
	intentName,
}: {
	g: RecallGuide;
	intentName: (k: string) => string;
}) {
	const facts = byFacts(g) ? <Badge tone="accent">按事实补</Badge> : null;
	if (g.standing) return <Badge tone="info">每轮都带</Badge>;
	return (
		<span className="inline-flex items-center gap-1">
			<span className="text-xs text-text-muted">相关性</span>
			{guideTags(g).length ? (
				<CellTags max={1} items={guideTags(g).map((k) => `${intentName(k)}＋`)} />
			) : null}
			{facts}
		</span>
	);
}

function GuideDetail({
	g,
	intentName,
	signalNames,
	layers,
}: {
	g: PlanGuide;
	intentName: (k: string) => string;
	signalNames?: Record<"sentiment" | "risk_flags", Record<string, string>>;
	layers: PlanLayer[];
}) {
	const conds = condItems(g);
	const signals = signalItems(g, signalNames);
	const previews = [
		{ key: "n2", label: "N2 决策（render_decide）", text: g.preview?.n2 },
		{ key: "n3", label: "N3 回复（render）", text: g.preview?.n3 },
	].filter((p) => p.text);
	return (
		<>
			<DrawerSection title="条件与做法">
				<Field label="适用条件（相关性判断按它判，也原样给 N2 ／ N3）">
					{g.condition_text || "—"}
				</Field>
				<Field label="做法">{g.action || "—"}</Field>
				<Field label="次数">
					{g.mode === "once"
						? "一次为限：本会话首次注入的那一轮内 N2 各步与 N3 都给，之后的轮次不再给"
						: "每轮：条件成立的每一轮都给"}
				</Field>
			</DrawerSection>
			<DrawerSection title="怎么召回">
				<Field label="召回方式">
					{g.standing
						? "常备：不经召回，每轮都带"
						: "相关性判断：建事项时按适用条件逐条判和这件事对不对得上（与工具排序同一次 Jev），相关度 ＋ 标签加分够进来线就召回；不再按意图 ／ 工具挂"}
					{byFacts(g) ? "；带数据 ／ 信息项条件，每步决策前再按事项当前事实补" : ""}
					<span className="ml-2 text-xs text-text-muted">
						进来线与加分见 <PageJump to="recall" />
					</span>
				</Field>
				<Field label="意图标签（只加分：诉求意图在里面时加分，不在也照样判相关）">
					{guideTags(g).map(intentName).join("、") || "无"}
					<span className="ml-2 text-xs text-text-muted">
						说明见 <PageJump to="tags" />
					</span>
				</Field>
				<Field label="情绪与风险标签（只加分：本轮信号命中时加分，不硬路由）">
					{signals.length ? signals.join("、") : "无"}
				</Field>
				<Field label="系统事实条件（数据状态 ／ 信息项，硬条件）">
					{conds.length ? conds.join("；") : "无"}
				</Field>
			</DrawerSection>
			<DrawerSection title="注入预览" testId="plan-guidance-preview">
				<p className="text-xs text-text-muted">
					这一条单独注入时给模型的原文（后端用运行时渲染函数出）；同轮多条时同一个标签里逐行排列，must
					在前。
				</p>
				{previews.length ? (
					previews.map((p) => (
						<Field key={p.key} label={p.label}>
							<pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-surface-border bg-surface-muted p-3 font-mono text-xs text-text-primary">
								{p.text}
							</pre>
						</Field>
					))
				) : (
					<span className="text-sm text-text-muted">—</span>
				)}
				{(g.status || "live") !== "live" ? (
					<p className="text-xs text-text-muted">已退役：运行时不注入，预览仅供对照。</p>
				) : null}
			</DrawerSection>
			<DrawerSection title="层" testId="plan-guidance-layer">
				<LayerDetail row={g} layers={layers} />
			</DrawerSection>
			<DrawerSection title="出处说明">
				<div className="whitespace-pre-wrap text-sm text-text-secondary">{g.rule_note || "—"}</div>
			</DrawerSection>
			{g.history ? (
				<DrawerSection title="变更史" testId="plan-guidance-history">
					{/* 画像 pv15 起出处说明只留定义，带日期 ／ 题号的变更史在这里（只进后台，不进模型） */}
					<div className="whitespace-pre-wrap text-xs text-text-secondary">{g.history}</div>
				</DrawerSection>
			) : null}
		</>
	);
}
